import gql from 'graphql-tag'
import { useEffect, useState } from 'react'
import { useApolloClient } from 'react-apollo'

// "Is this the same place?" (CVL-163): existing places with the same name as the venue being
// typed, in its town or within about 200 m. The event form and the add-a-place form ask once a
// name is known, even with no town (the server then matches by distance only); only the answer to
// the latest check is shown, and none once the form is closed. With no town, the event form also
// sends its picked community, which the server uses in place of the town (CVL-168).
export const SimilarPlacesQuery = gql`
  query SimilarPlaces($name: String!, $addressCity: String, $geo: Geo, $primaryCommunityId: ID) {
    similarPlaces(name: $name, addressCity: $addressCity, geo: $geo, primaryCommunityId: $primaryCommunityId) {
      id
      slug
      name
      addressStreetAddress
      addressCity
      addressState
    }
  }
`

export type SimilarPlace = {
  id: string
  // The place page is found by slug, not id.
  slug: string
  name: string
  addressStreetAddress: string | null
  addressCity: string | null
  addressState: string | null
}

type Geo = { type?: string; geometry?: { type?: string; coordinates?: number[] } } | null | undefined

type Params = {
  name?: string | null
  addressCity?: string | null
  geo?: Geo
  // Used only when there is no town.
  primaryCommunityId?: string | null
  enabled?: boolean
  // Changing it asks again for the same name and town (after saving is refused as a duplicate).
  refreshKey?: number
}

const DEBOUNCE_DELAY = 500

const hasCoordinates = (geo: Geo): boolean => {
  const coordinates = geo?.geometry?.coordinates
  return Boolean(coordinates && typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number')
}

const useSimilarPlaces = ({ name, addressCity, geo, primaryCommunityId, enabled = true, refreshKey = 0 }: Params): SimilarPlace[] => {
  const [places, setPlaces] = useState<SimilarPlace[]>([])
  const client = useApolloClient()

  const trimmedName = (name || '').trim()
  const canCheck = Boolean(enabled && trimmedName.length >= 2)
  const key = JSON.stringify({
    name: trimmedName,
    addressCity: addressCity || null,
    geo: hasCoordinates(geo) ? { type: 'Feature', geometry: geo?.geometry } : null,
    primaryCommunityId: (!addressCity && primaryCommunityId) || null,
  })

  useEffect(() => {
    let current = true
    if (!canCheck) {
      setPlaces([])
      return
    }

    const timer = setTimeout(() => {
      client
        .query<{ similarPlaces?: SimilarPlace[] }>({ query: SimilarPlacesQuery, fetchPolicy: 'network-only', variables: JSON.parse(key) })
        .then(({ data }) => {
          if (current) setPlaces(data?.similarPlaces || [])
        })
        .catch(error => {
          // Non-critical: saving still asks on the add-a-place form.
          console.error('Similar places check failed:', error.message)
        })
    }, DEBOUNCE_DELAY)

    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [canCheck, client, key, refreshKey])

  return places
}

export default useSimilarPlaces
